import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, 
  User, 
  Folder, 
  Shield, 
  Bell,
  Key,
  Save,
  RefreshCw,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

interface UserSettings {
  name: string;
  email: string;
  role: string;
  timezone: string;
  notifications: {
    email: boolean;
    push: boolean;
    compliance: boolean;
    deadlines: boolean;
  };
}

interface SyncSettings {
  dropboxEnabled: boolean;
  dropboxPath: string;
  autoSync: boolean;
  syncFrequency: string;
  backupEnabled: boolean;
  encryptionEnabled: boolean;
}

interface SecuritySettings {
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  ipWhitelist: string[];
  auditLogging: boolean;
  dataRetention: number;
}

const WealthOSSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('user');
  const [userSettings, setUserSettings] = useState<UserSettings>({
    name: 'Dana Patterson',
    email: 'dana@sicadvisors.com',
    role: 'Administrator',
    timezone: 'America/New_York',
    notifications: {
      email: true,
      push: true,
      compliance: true,
      deadlines: true
    }
  });
  
  const [syncSettings, setSyncSettings] = useState<SyncSettings>({
    dropboxEnabled: true,
    dropboxPath: '/WealthOS/Documents/',
    autoSync: true,
    syncFrequency: '15min',
    backupEnabled: true,
    encryptionEnabled: true
  });
  
  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    twoFactorEnabled: true,
    sessionTimeout: 480,
    ipWhitelist: [],
    auditLogging: true,
    dataRetention: 2555
  });
  
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const handleSave = async () => {
    setSaving(true);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setLastSaved(new Date());
    setSaving(false);
  };

  const tabs = [
    { id: 'user', name: 'User Profile', icon: User },
    { id: 'sync', name: 'File Sync', icon: Folder },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell }
  ];

  const renderUserSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Full Name
        </label>
        <input
          type="text"
          value={userSettings.name}
          onChange={(e) => setUserSettings(prev => ({ ...prev, name: e.target.value }))}
          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Email Address
        </label>
        <input
          type="email"
          value={userSettings.email}
          onChange={(e) => setUserSettings(prev => ({ ...prev, email: e.target.value }))}
          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Role
        </label>
        <select
          value={userSettings.role}
          onChange={(e) => setUserSettings(prev => ({ ...prev, role: e.target.value }))}
          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
        >
          <option value="Administrator">Administrator</option>
          <option value="Tax Preparer">Tax Preparer</option>
          <option value="Viewer">Viewer</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Timezone
        </label>
        <select
          value={userSettings.timezone}
          onChange={(e) => setUserSettings(prev => ({ ...prev, timezone: e.target.value }))}
          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
        >
          <option value="America/New_York">Eastern Time</option>
          <option value="America/Chicago">Central Time</option>
          <option value="America/Denver">Mountain Time</option>
          <option value="America/Los_Angeles">Pacific Time</option>
        </select>
      </div>
    </div>
  );

  const renderSyncSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Folder className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h4 className="font-medium text-white">Dropbox Integration</h4>
            <p className="text-sm text-slate-400">Sync files with Dropbox for automated processing</p>
          </div>
        </div>
        
        <button
          onClick={() => setSyncSettings(prev => ({ ...prev, dropboxEnabled: !prev.dropboxEnabled }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            syncSettings.dropboxEnabled ? 'bg-teal-600' : 'bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              syncSettings.dropboxEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      
      {syncSettings.dropboxEnabled && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Dropbox Folder Path
            </label>
            <input
              type="text"
              value={syncSettings.dropboxPath}
              onChange={(e) => setSyncSettings(prev => ({ ...prev, dropboxPath: e.target.value }))}
              className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              placeholder="/WealthOS/Documents/"
            />
            <p className="text-xs text-slate-400 mt-1">Files in this folder will be automatically processed</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Sync Frequency
            </label>
            <select
              value={syncSettings.syncFrequency}
              onChange={(e) => setSyncSettings(prev => ({ ...prev, syncFrequency: e.target.value }))}
              className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            >
              <option value="5min">Every 5 minutes</option>
              <option value="15min">Every 15 minutes</option>
              <option value="30min">Every 30 minutes</option>
              <option value="1hour">Every hour</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-white">Auto-sync new files</h4>
              <p className="text-xs text-slate-400">Automatically process new files as they're added</p>
            </div>
            <button
              onClick={() => setSyncSettings(prev => ({ ...prev, autoSync: !prev.autoSync }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                syncSettings.autoSync ? 'bg-teal-600' : 'bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  syncSettings.autoSync ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-white">Enable backups</h4>
              <p className="text-xs text-slate-400">Create backup copies of processed files</p>
            </div>
            <button
              onClick={() => setSyncSettings(prev => ({ ...prev, backupEnabled: !prev.backupEnabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                syncSettings.backupEnabled ? 'bg-teal-600' : 'bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  syncSettings.backupEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-500/20 rounded-lg">
            <Key className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h4 className="font-medium text-white">Two-Factor Authentication</h4>
            <p className="text-sm text-slate-400">Add an extra layer of security to your account</p>
          </div>
        </div>
        
        <button
          onClick={() => setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: !prev.twoFactorEnabled }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            securitySettings.twoFactorEnabled ? 'bg-teal-600' : 'bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              securitySettings.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Session Timeout (minutes)
        </label>
        <input
          type="number"
          value={securitySettings.sessionTimeout}
          onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
          min="15"
          max="1440"
        />
        <p className="text-xs text-slate-400 mt-1">Automatically log out after this period of inactivity</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Data Retention (days)
        </label>
        <input
          type="number"
          value={securitySettings.dataRetention}
          onChange={(e) => setSecuritySettings(prev => ({ ...prev, dataRetention: parseInt(e.target.value) }))}
          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
          min="365"
          max="3650"
        />
        <p className="text-xs text-slate-400 mt-1">How long to keep audit logs and processed data</p>
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-white">Audit Logging</h4>
          <p className="text-xs text-slate-400">Log all user actions for compliance</p>
        </div>
        <button
          onClick={() => setSecuritySettings(prev => ({ ...prev, auditLogging: !prev.auditLogging }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            securitySettings.auditLogging ? 'bg-teal-600' : 'bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              securitySettings.auditLogging ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Bell className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h4 className="font-medium text-white">Email Notifications</h4>
            <p className="text-sm text-slate-400">Receive updates via email</p>
          </div>
        </div>
        
        <button
          onClick={() => setUserSettings(prev => ({ 
            ...prev, 
            notifications: { ...prev.notifications, email: !prev.notifications.email }
          }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            userSettings.notifications.email ? 'bg-teal-600' : 'bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              userSettings.notifications.email ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      
      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-500/20 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h4 className="font-medium text-white">Compliance Alerts</h4>
            <p className="text-sm text-slate-400">Get notified about compliance issues</p>
          </div>
        </div>
        
        <button
          onClick={() => setUserSettings(prev => ({ 
            ...prev, 
            notifications: { ...prev.notifications, compliance: !prev.notifications.compliance }
          }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            userSettings.notifications.compliance ? 'bg-teal-600' : 'bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              userSettings.notifications.compliance ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      
      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h4 className="font-medium text-white">Deadline Reminders</h4>
            <p className="text-sm text-slate-400">Reminders for tax filing deadlines</p>
          </div>
        </div>
        
        <button
          onClick={() => setUserSettings(prev => ({ 
            ...prev, 
            notifications: { ...prev.notifications, deadlines: !prev.notifications.deadlines }
          }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            userSettings.notifications.deadlines ? 'bg-teal-600' : 'bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              userSettings.notifications.deadlines ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-full bg-slate-900 p-6 overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-500/20 rounded-lg">
              <Settings className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white text-drop-shadow">Settings</h1>
              <p className="text-slate-400">Configure your WealthOS preferences</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {lastSaved && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>Saved {lastSaved.toLocaleTimeString()}</span>
              </div>
            )}
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 rounded-lg text-teal-400 font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                        : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
          
          <div className="lg:col-span-3">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
            >
              {activeTab === 'user' && renderUserSettings()}
              {activeTab === 'sync' && renderSyncSettings()}
              {activeTab === 'security' && renderSecuritySettings()}
              {activeTab === 'notifications' && renderNotificationSettings()}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WealthOSSettings;
